// Instance tags for categorizing workload types
// Backend returns: 'ssh', 'ssh-vllm', 'ssh-triton', etc.
export type InstanceTag =
  | 'ssh'           // Base SSH instance
  | 'ssh-vllm'      // vLLM deployment
  | 'ssh-triton'    // Triton deployment
  | 'ssh-tao'       // TAO training
  | 'ssh-monai'     // MONAI medical imaging
  | 'ssh-nemo'      // NeMo speech/NLP
  | 'ssh-float16'   // Float16 training
  | 'ssh-ocr'       // Thai Document OCR
  // Legacy values for backward compatibility
  | 'vllm' | 'triton' | 'tao' | 'monai' | 'nemo' | 'float16-training' | 'ocr'
  | null;

export const InstanceTagLabel: Record<NonNullable<InstanceTag>, string> = {
  // New format from backend
  'ssh': 'SSH',
  'ssh-vllm': 'vLLM',
  'ssh-triton': 'Triton Inference Server',
  'ssh-tao': 'TAO',
  'ssh-monai': 'MONAI',
  'ssh-nemo': 'NeMo',
  'ssh-float16': 'Float16 Training',
  'ssh-ocr': 'Thai Document OCR',
  // Legacy format (backward compatibility)
  'vllm': 'vLLM',
  'triton': 'Triton Inference Server',
  'tao': 'TAO',
  'monai': 'MONAI',
  'nemo': 'NeMo',
  'float16-training': 'Float16 Training',
  'ocr': 'Thai Document OCR',
};

export enum SSHInstanceStatus {
  Pending = 'pending',
  Running = 'running',
  SSHReady = 'ssh_ready',
  Stopped = 'stopped',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
  Terminated = 'terminated',
}

// Display labels - merges similar statuses for UI
// Running + SSH Ready => Running
// Completed + Terminated + Cancelled => Terminated
export const SSHInstanceStatusLabel: Record<SSHInstanceStatus, string> = {
  [SSHInstanceStatus.Pending]: 'Pending',
  [SSHInstanceStatus.Running]: 'Running',
  [SSHInstanceStatus.SSHReady]: 'Running',
  [SSHInstanceStatus.Stopped]: 'Stopped',
  [SSHInstanceStatus.Completed]: 'Terminated',
  [SSHInstanceStatus.Failed]: 'Failed',
  [SSHInstanceStatus.Cancelled]: 'Terminated',
  [SSHInstanceStatus.Terminated]: 'Terminated',
};

// Unique status options for filter dropdown
export const SSHInstanceStatusFilterOptions = [
  { label: 'Pending', values: [SSHInstanceStatus.Pending] },
  { label: 'Running', values: [SSHInstanceStatus.Running, SSHInstanceStatus.SSHReady] },
  { label: 'Stopped', values: [SSHInstanceStatus.Stopped] },
  { label: 'Terminated', values: [SSHInstanceStatus.Completed, SSHInstanceStatus.Cancelled, SSHInstanceStatus.Terminated] },
  { label: 'Failed', values: [SSHInstanceStatus.Failed] },
];

// Use case types for playground features
export type InstanceUseCase = 'text' | 'vision' | 'reasoning' | 'tools' | 'grammar' | 'embedding' | 'typhoon-ocr';

export interface SSHInstance {
  task_id: string;
  task_name: string;
  parent_task_id?: string;  // Links instances across stop/resume cycles
  status: SSHInstanceStatus;
  start_time: number | null;
  end_time: number | null;
  project_id: string;
  project_name: string;
  gpu_type: string;
  instance_ip: string | null;
  created_at: number | null;
  deleted_at: number | null;
  instance_tag?: InstanceTag;
  endpoint_url?: string;
  use_case?: InstanceUseCase[] | null;  // Enables playground features based on model abilities
}

export interface ListSSHInstancesResponse {
  status: string;
  instances: SSHInstance[];
  total: number;
}

export type GPUInstance = SSHInstance;

export interface RunSSHPayload {
  gpu_type: string;
  volume_size: number;
  rootfs_size: number;
  project_name?: string;
  container_image?: string;
  task_name?: string;
  vcpu?: number;
  memory_gb?: number;
  args?: string;
  instance_tag?: InstanceTag;
  use_case?: InstanceUseCase[];  // Model abilities for playground features
  // vLLM model support overrides (for container_image=vllm)
  tool_parser?: string;  // e.g., 'hermes', 'llama3_json', 'mistral'
  reasoning_template?: string;  // e.g., 'qwen3', 'deepseek_r1'
}

export interface GetGPUInstanceByIdRequest {
  instance_id: string;
}

export interface GetGPUInstanceByIdResponse extends GPUInstance {}

// SSE Event Data Types
export interface SSESSHKeyData {
  private_key: string;
}

export interface SSESSHReadyData {
  host: string;
  port: number;
}

export interface TerminateSSHResponse {
  status: string;
  task_id: string;
  details: string;
}

// Common response type for task actions (stop/resume/terminate)
export interface TaskActionResponse {
  status: string;
  task_id: string;
  details: string;
}

// Task Logs Types
export interface LogEntry {
  timestamp: string;  // nanoseconds as string
  message: string;
}

// Stage types for task progress
export type StageStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface DockerPullStage {
  stage: 'docker_pull';
  status: StageStatus;
  image?: string;
  elapsed_seconds?: number;
}

export interface HfDownloadStage {
  stage: 'hf_download';
  status: StageStatus;
  model_id?: string;
  total_size_human?: string;
  file_count?: number;
}

export interface VllmServerStage {
  stage: 'vllm_server';
  status: StageStatus;
  api_ready?: boolean;
  served_model?: string;
}

export type TaskStage = DockerPullStage | HfDownloadStage | VllmServerStage;

export interface TaskLogsResponse {
  status: string;
  task_id: string;
  stages: TaskStage[];
  logs: LogEntry[];
  total: number;
}

// Model Validation Types (Legacy)
export interface ValidateModelPayload {
  model_id: string;  // HuggingFace format: org/model-name
  framework: 'vllm' | 'triton';
}

export interface ModelValidationInfo {
  model_id: string;
  name: string;
  parameters_b: number;
  vram_required: number;
  framework_compatible: boolean;
  accessible: boolean;
  license?: string;
}

export type ModelValidationErrorCode =
  | 'MODEL_NOT_FOUND'
  | 'MODEL_PRIVATE'
  | 'MODEL_GATED'
  | 'FRAMEWORK_INCOMPATIBLE'
  | 'NETWORK_ERROR';

export interface ModelValidationError {
  code: ModelValidationErrorCode;
  message: string;
}

export interface ValidateModelResponse {
  status: string;
  model?: ModelValidationInfo;
  error?: ModelValidationError;
}

// Model Support Matrix Types (New endpoint: GET /model/support-matrix)
export interface ModelSupportMatrixResponse {
  repo: string;
  detection_source: 'manual' | 'programmatic';
  family: string;
  supports_vision: boolean;
  supports_video: boolean;
  supports_reasoning: boolean;
  reasoning_template?: string | null;
  tool_parser?: string | null;
}

export interface ModelSupportMatrixError {
  error: string;
  repo: string;
}

export type ModelSupportMatrixResult = ModelSupportMatrixResponse | ModelSupportMatrixError;

// Type guard to check if the result is an error
export const isModelSupportMatrixError = (
  result: ModelSupportMatrixResult
): result is ModelSupportMatrixError => {
  return 'error' in result;
};
